import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Input,
  Textarea,
  FormLabel,
  FormControl,
  FormErrorMessage,
  Select,
  Text,
  SimpleGrid,
  Spinner,
  HStack,
  Badge,
  Switch
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { motion } from "framer-motion";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";

const InsertServices = () => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [translationKey, setTranslationKey] = useState("");
  const [fee, setFee] = useState("");
  const [image, setImage] = useState("");
  const [representativeImage, setRepresentativeImage] = useState("");
  const [status, setStatus] = useState("on");
  const [translations, setTranslations] = useState({ fr: "", it: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});
  const [imageSize, setImageSize] = useState(null);
  const [representativeImageSize, setRepresentativeImageSize] = useState(null);

  const imageInputRef = useRef(null);
  const representativeImageInputRef = useRef(null);

  // Helper for image size validation (max 1MB)
  const validateImageSize = (file) => file.size <= 1024 * 1024;
  const pageId = 13;
  // Convert the selected file to a base64 string with size check
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageSize(file.size);
      if (!validateImageSize(file)) {
        setFieldErrors((prev) => ({ ...prev, image: t("Image must be less than 1MB") }));
        return;
      }
      setFieldErrors((prev) => ({ ...prev, image: undefined }));
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setImage(reader.result);
      };
    } else {
      setImageSize(null);
    }
  };

  const handleRepresentativeImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setRepresentativeImageSize(file.size);
      if (!validateImageSize(file)) {
        setFieldErrors((prev) => ({ ...prev, representativeImage: t("Image must be less than 1MB") }));
        return;
      }
      setFieldErrors((prev) => ({ ...prev, representativeImage: undefined }));
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setRepresentativeImage(reader.result);
      };
    } else {
      setRepresentativeImageSize(null);
    }
  };

  const handleTranslationChange = (lang, value) => {
    setTranslations({ ...translations, [lang]: value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);


    // Validate fee
    if (!fee || isNaN(fee)) {
      setFieldErrors((prev) => ({ ...prev, fee: t("The fee field is required.") }));
      setIsSubmitting(false);
      return;
    } else {
      setFieldErrors((prev) => ({ ...prev, fee: undefined }));
    }

    const data = {
      name,
      description,
      translation_key: translationKey,
      fee: parseFloat(fee),
      ...(image ? { image } : {}),
      ...(representativeImage ? { representative_image: representativeImage } : {}),
      status,
      translations,
    };

    try {
      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/services?page_id=${pageId}`,
        "POST",
        data
      );

  setName("");
  setDescription("");
  setTranslationKey("");
  setFee("");
  setImage("");
  setRepresentativeImage("");
  setStatus("on");
  setTranslations({ fr: "", it: "" });
  setError("");
  setSuccess(true);

      // Reset the file input elements
      if (imageInputRef.current) imageInputRef.current.value = "";
      if (representativeImageInputRef.current) representativeImageInputRef.current.value = "";

      setTimeout(() => setSuccess(false), 2000);
    } catch (error) {
      const errorLines = error.message ? error.message.split("\n") : [t("Please try again later.")];
      // toast({
      //   title: t("Error"),
      //   description: (
      //     <>
      //       {errorLines.map((line, i) => (
      //         <div key={i}>{line}</div>
      //       ))}
      //     </>
      //   ),
      //   status: "error",
      //   position: "top-right",
      //   duration: 5000,
      //   isClosable: true,
      // });
      setError(errorLines || t("Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.5 }}
    >
      <Card p={{ base: "20px", md: "30px" }} >
        <form onSubmit={handleSubmit}>
          {error && (
          <Box color="red" mt={2}>
            {Array.isArray(error)
              ? error.map((line, i) => <div key={i}>{line}</div>)
              : <div>{error}</div>
            }
          </Box>
        )}
          {success && (
            <Text color="green.500" textAlign="center">
              {t("Service added successfully!")}
            </Text>
          )}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={6}>
           
            {/* Name */}
            <FormControl isRequired isInvalid={!!fieldErrors.name}>
              <FormLabel color="blue.600">{t("Name")}</FormLabel>
              <Input
                placeholder={t("Name")}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <FormErrorMessage>{fieldErrors.name}</FormErrorMessage>
            </FormControl>
            {/* Description */}
            <FormControl isRequired isInvalid={!!fieldErrors.description}>
              <FormLabel color="blue.600">{t("Description")}</FormLabel>
              <Textarea
                placeholder={t("Description")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                 minH="40px"
                size="sm"
                borderRadius={"8px"}
              />
              <FormErrorMessage>{fieldErrors.description}</FormErrorMessage>
            </FormControl>
            {/* Translation Key */}
            <FormControl isRequired isInvalid={!!fieldErrors.translationKey}>
              <FormLabel color="blue.600">{t("Translation Key")}</FormLabel>
              <Input
                placeholder={t("Translation Key")}
                type="text"
                value={translationKey}
                onChange={(e) => setTranslationKey(e.target.value)}
                required
              />
              <FormErrorMessage>{fieldErrors.translationKey}</FormErrorMessage>
            </FormControl>
            {/* Translation (French) */}
            <FormControl isRequired isInvalid={!!fieldErrors.translationKey}>
 <FormLabel color="blue.600">{t("Translation ")} 
                <Text 
                //bold text
                as="span" fontWeight="bold"
                >
                  {t("(French)")} 
                </Text>
              </FormLabel>              <Input
                placeholder={t("Translation (French)")}
                type="text"
                value={translations.fr}
                onChange={(e) => handleTranslationChange("fr", e.target.value)}
              />
            </FormControl>
            {/* Translation (Italy) */}
            <FormControl isRequired isInvalid={!!fieldErrors.translationKey}>
<FormLabel color="blue.600">{t("Translation ")}
                <Text 
                  as="span" fontWeight="bold"
                >
                  {t("(Italy)")} 
                </Text>
              </FormLabel>              <Input
                placeholder={t("Translation (Italy)")}
                type="text"
                value={translations.it}
                onChange={(e) => handleTranslationChange("it", e.target.value)}
              />
            </FormControl>
             {/* Fee */}
            <FormControl isRequired isInvalid={!!fieldErrors.fee}>
              <FormLabel color="blue.600">{t("Fee")}</FormLabel>
              <Input
                placeholder={t("Fee")}
                type="number"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
                required
              />
              <FormErrorMessage>{fieldErrors.fee}</FormErrorMessage>
            </FormControl>
          
                        {/* Image Upload */}
            <FormControl isInvalid={!!fieldErrors.image}>
              <FormLabel color="blue.600">{t("Image")}</FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                ref={imageInputRef}
              />
               {/* Always show the helper text */}
    <Text fontSize="sm" color="gray.500" mt={2}>
      {t("Recommended size: Max 1 MB (≈1024 KB), up to 1024×1024 pixels")}
    </Text>
              {imageSize !== null && (
                <Text fontSize="xs" color={imageSize > 1024 * 1024 ? "red.500" : "gray.500"} mt={1}>
                  {t("Selected file size")}: {(imageSize / 1024).toFixed(1)} KB
                  {imageSize > 1024 * 1024 && ` (${t("Too large")})`}
                </Text>
              )}
              <FormErrorMessage>{fieldErrors.image}</FormErrorMessage>
            </FormControl>
            {/* Representative Image Upload */}
            <FormControl isInvalid={!!fieldErrors.representativeImage}>
              <FormLabel color="blue.600">{t("Service Profile")}</FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleRepresentativeImageUpload}
                ref={representativeImageInputRef}
              />
               {/* Always show the helper text */}
    <Text fontSize="sm" color="gray.500" mt={2}>
      {t("Recommended size: Max 1 MB (≈1024 KB), up to 1024×1024 pixels")}
    </Text>
              {representativeImageSize !== null && (
                <Text fontSize="xs" color={representativeImageSize > 1024 * 1024 ? "red.500" : "gray.500"} mt={1}>
                  {t("Selected file size")}: {(representativeImageSize / 1024).toFixed(1)} KB
                  {representativeImageSize > 1024 * 1024 && ` (${t("Too large")})`}
                </Text>
              )}
              <FormErrorMessage>{fieldErrors.representativeImage}</FormErrorMessage>
            </FormControl>
              {/* Status */}
            <FormControl isRequired>
              <FormLabel color="blue.600">{t("Status")}</FormLabel>
            
  <HStack spacing={4}>
    {/* Switch for toggling status */}
    <Switch
      isChecked={status === "on"}
      onChange={(e) => setStatus(e.target.checked ? "on" : "off")}
      colorScheme="cyan"
    />
    {/* Badge to display current status */}
    <Badge
      colorScheme={status === "on" ? "green" : "red"}
      variant="solid"
      px={3}
      py={1}
      borderRadius="md"
    >
      {status === "on" ? t("Active") : t("Inactive")}
    </Badge>
  </HStack>
</FormControl>
            
            
          </SimpleGrid>
          <Box>
            <Button
              mt={4}
              colorScheme="cyan"
              fontSize="m"
              borderRadius="16px"
              w={{ base: "100%", md: "148px" }}
              h="46px"
              type="submit"
              color="white"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner size="sm" /> : t("Submit")}
            </Button>
          </Box>
          {/* Show all selected images with labels at the bottom */}
          <Box mt={8}>
            <Text fontWeight="bold" mb={2}>{t("Selected Images")}</Text>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing={6}>
              {image && (
                <Box textAlign="center">
                  <Text fontSize="sm" mb={1}>{t("Image")}</Text>
                  <img src={image} alt="Image Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                </Box>
              )}
              {representativeImage && (
                <Box textAlign="center">
                  <Text fontSize="sm" mb={1}>{t("Service Profile")}</Text>
                  <img src={representativeImage} alt="Profile Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                </Box>
              )}
            </SimpleGrid>
          </Box>
        </form>
      </Card>
    </motion.div>
  );
};

export default InsertServices;